import { ClientConfig } from '../../config/ClientConfig'
import { _noop, _queryParameterize } from '../../script-utils'
import { HttpService } from '../../services/http-service'
import { PlayerTransactionHistory, WalletAccountType } from './types'
import { AuthService } from '../../auth/AuthService'
import { fromPromise } from 'rxjs/internal/observable/fromPromise'
import { PlayerNote } from '../PlayerApiProtocol.generated'
import { myPlayer } from '../../MyPlayer'
import { getGeoHeaders } from '../../services/GeoLocationService'

export const PlayerAccountApi = new class {
	getItemsForUser(): Promise<GetItemsForUserResponse> {
		return AuthService.getCurrentUserId()
			.then(userId => `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/players/${userId}/items`)
			.then(url => HttpService.get<GetItemsForUserResponse>(url, { authenticate: true }))
	}

	getItemGroupingsForUser(): Promise<GetItemGroupingsForUserResponse> {
		return AuthService.getCurrentUserId()
			.then(userId => `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/players/${userId}/item-groupings`)
			.then(url => HttpService.get<GetItemGroupingsForUserResponse>(url, { authenticate: true }))
	}

	async getTransactionHistoryForUser(
		request: TransactionHistoryRequest,
	): Promise<PlayerTransactionHistory> {
		const userId = await AuthService.getCurrentUserId()
		const path = `/wallet/v1/${userId}/${request.accountType}/transactions`
		const query = _queryParameterize({
			limit: request.limit,
			offset: request.offset,
			expandTotalResults: request.expandTotalResults,
			timeStart: request.timeRange?.startTime.toISOString(),
			timeEnd: request.timeRange?.endTime.toISOString(),
		})
		const url = ClientConfig.PLAYER_ACCOUNT_BASE_URL + path + query

		return HttpService.get<TransactionHistoryResponse>(url, { authenticate: true }).then(res => {
			return {
				transactions: res.transactions.map(tx => ({
					userId: tx.userId,
					accountType: tx.accountType as WalletAccountType,
					comment: tx.comment,
					balance: parseFloat(tx.balance),
					amount: parseFloat(tx.amount),
					timestamp: new Date(tx.timestamp),
				})),
				totalResultsCount: res.totalResultsCount,
			}
		})
	}

	async claimEndlessPlayBonus(): Promise<boolean> {
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/endless-play/claim`

		return HttpService.post<ClaimEndlessPlayResult>(url, {}, { authenticate: true }).then((r) => {
			return r.wasClaimed
		})
	}

	async getGoldCoinsVault() {
		return this.getBalance('gc-vault')
	}
	async getSweepCoinsVault() {
		return this.getBalance('sc-vault')
	}
	private async getBalance(accountType: string) {
		return AuthService.getCurrentUserId()
			.then(userId => `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/wallet/v1/${userId}/${accountType}/balance`)
			.then(url => HttpService.get<number>(url, { authenticate: true }))
	}

	async claimGoldCoinsVault() {
		return this.claimVault('gc')
	}
	async claimSweepCoinsVault() {
		return this.claimVault('sc')
	}
	private async claimVault(coinType: string) {
		return HttpService.post<void>(`${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/vault/claim/${coinType}`, null, { authenticate: true })
	}

	async fetchUserSelfExclusion(): Promise<SelfExclusionResponse | EmptyResponse> {
		const userId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/user/${userId}/self-exclusions/active`
		return HttpService.get<SelfExclusionResponse>(url, { authenticate: true })
	}

	async createUserSelfExclusion(period: string) {
		const userId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/user/${userId}/self-exclusions`
		return fromPromise(HttpService.post(url, { period, }, { authenticate: true }))
	}

	async getRecentlyPlayedGames() {
		const userId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/recently-played/${userId}`
		return HttpService.get<RecentlyPlayedResponse>(url, { authenticate: true })
	}

	async updateRecentlyPlayedGame(gameId: string) {
		const userId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/recently-played/${userId}`
		return HttpService.post<RecentlyPlayedResponse>(url, { gameId }, { authenticate: true })
	}

	savePlayerNote(note: PlayerNote, onSuccess: () => void, onError: ErrorCallback) {
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/poker/v1/${myPlayer.id}/observations/${note.playerId}/notes`
		HttpService.put(url, { category: note.category, notes: note.text }, { authenticate: true }).then(onSuccess).catch(onError)
	}

	requestPlayerNotes(playerIds: number[], onSuccess: SuccessCallback<PlayerNote[]>, onError: ErrorCallback = _noop) {
		const params = new URLSearchParams({
			notedPlayerUserId: playerIds.toString(),
		})
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/poker/v1/${myPlayer.id}/observations/notes?${params.toString()}`
		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	async fetchReferralCode(): Promise<ReferralCodeResponse> {
		const playerId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/referrals/player/${playerId}`
		return HttpService.get<ReferralCodeResponse>(url, { authenticate: true })
	}

	async createReferralCode(): Promise<void> {
		const playerId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/referrals/${playerId}/code`
		return HttpService.post(url, {}, { authenticate: true })
	}

	async fetchReferralStats(): Promise<ReferralStatsResponse> {
		const playerId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/referrals/stats/${playerId}`
		return HttpService.get<ReferralStatsResponse>(url, { authenticate: true })
	}

	async claimReferrerRewards(referralCode: string): Promise<void> {
		const playerId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/referrals/${playerId}/referral-claims`
		const requestBody: ReferrerClaimRewardsRequest = {
			referralCode,
			claimedAt: new Date().toISOString()
		}
		const geoHeaders = getGeoHeaders()
		return HttpService.post<void>(url, requestBody, { authenticate: true, ...(geoHeaders && { headers: geoHeaders }) })
	}

	async fetchRefereeStatus(): Promise<RefereeStatusResponse> {
		const playerId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/referrals/player/${playerId}/referee-status`
		return HttpService.get<RefereeStatusResponse>(url, { authenticate: true })
	}

	async claimRefereeReward(): Promise<void> {
		const playerId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/referrals/${playerId}/referee-claims`
		const requestBody: RefereeClaimRewardRequest = {
			claimedAt: new Date().toISOString()
		}
		const geoHeaders = getGeoHeaders()
		return HttpService.post<void>(url, requestBody, { authenticate: true, ...(geoHeaders && { headers: geoHeaders }) })
	}

	async nudgeReferee(refereeId: number): Promise<void> {
		const url = `${ClientConfig.PLAYER_ACCOUNT_BASE_URL}/referrals/${refereeId}/nudge`
		return HttpService.post<void>(url, {}, { authenticate: true })
	}
}()

export interface TransactionHistoryRequest {
    accountType: WalletAccountType;
    limit: number;
    offset: number;
    expandTotalResults: boolean;
    timeRange?: TimeRange;
}

interface TransactionHistoryResponse {
    transactions: Array<{
        accountType: string;
        amount: string;
        balance: string;
        comment: string;
        timestamp: string;
        userId: number;
    }>;
    totalResultsCount?: number;
}

export interface GetItemsForUserResponse {
    items: PlayerItem[];
}

export interface GetItemGroupingsForUserResponse {
    items: PlayerItemGrouping[];
}

type Category = 'FREE_SPINS'
export type Tier = 'COMMON' | 'UNCOMMON' | 'RARE' | 'LEGENDARY'
export interface PlayerItem {
    id: string;
    category: Category
    catalogItemId: string;
    slot: string;
    coinType: string;
    spinCost: number;
    expirationDate: string;
    tier: string;
}

interface ClaimEndlessPlayResult {
    wasClaimed: boolean;
}

export interface PlayerItemGrouping {
    catalogItemId: string,
    category: string
    slot: string
    quantity: number
    tier: Tier
    coinType: string
    expirationDate: string
}

export type SelfExclusionResponse = {
    id: string;
    userId: number;
    timePeriod: string;
    startDate: string;
    endDate: string;
    createdBy: string;
    removedAt: string;
    removedBy: string;
    removedByEmail: string;
}

export type RecentlyPlayedGame = {
	gameId: string;
	lastPlayed: string;
}

export type ReferralTierResponse = {
	level: number
	name: string
	referrerGcAmount: number
	referrerScAmount: number
}

export type ReferralNextTierResponse = ReferralTierResponse & {
	referralsRequired: number
	referralsRemaining: number
}

export type ReferralTierDefinitionResponse = {
	level: number
	name: string
	referralsToUnlock: number
	referrerGcAmount: number
	referrerScAmount: number
}

export type ReferralTotalEarningsResponse = {
	gc: number
	sc: number
}

export type ReferralRefereeResponse = {
	playerId: number
	status: 'PENDING' | 'COMPLETED' | 'CLAIMED'
	cddVerified: boolean
	firstPurchase: boolean
	joinedAt: string
	completedAt: string | null
	nudgedAt: string | null
}

export type ReferralStatsResponse = {
	numberOfFriendsJoined: number
	numberOfPendingReferrals: number
	numberOfCompletedReferrals: number
	numberOfUnclaimedReferrals: number
	currentTier: ReferralTierResponse | null
	nextTier: ReferralNextTierResponse | null
	totalEarnings: ReferralTotalEarningsResponse
	tiers: ReferralTierDefinitionResponse[]
	referees: ReferralRefereeResponse[]
}

export type RecentlyPlayedResponse = {
	count: number;
	games: RecentlyPlayedGame[];
}

export type EmptyResponse = {
}

export type ReferralCodeResponse = {
	code: string
	status: ReferralCodeStatus
}

export type ReferralCodeStatus = 'ACTIVE' | 'INACTIVE'

export interface ReferrerClaimRewardsRequest {
	referralCode: string
	claimedAt: string
}

export interface RefereeClaimRewardRequest {
	claimedAt: string
}

export type RefereeStatusResponse = {
	isReferee: boolean
	hasCompletedFirstPurchase: boolean
	isKycVerified: boolean
	hasClaimed: boolean
}

export function isSelfExclusionResponse(obj: any): obj is SelfExclusionResponse {
	return obj.userId !== undefined
}

type SuccessCallback<T> = (response: T) => void
type ErrorCallback = () => void
